import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize, firstValueFrom } from 'rxjs';
import { CustomClaimService } from '../../core/services/custom-claim.service';
import { ToastService } from '../../core/services/toast.service';
import { MembershipRoles } from '../../core/data-models/membership-roles.model';
import { ConfirmationModalComponent } from '../../shared/components/confirmation-modal.component';
import { CertificationsCardComponent } from '../../shared/components/certifications-card/certifications-card.component';

import { ApiError } from '../../abstract/api-error';
import { AdminInvitationService } from '../../core/data-services/admin-invitation.service';
import { UserService } from '../../core/data-services/user.service';
import { User } from '../../core/data-models/user.model';
import { UserInvitation } from '../../core/data-models/user-invitation.model';
import { Coordinates } from '../../core/response-models/coordinates.model';
import { UserClaims } from '../../core/response-models/user-claims.model';
import { optionalText } from '../../core/utils/form-value-utils';
import { LoginPlatform, getLoginPlatformBadge } from '../../shared/utils/login-platform.utils';
import { getMembershipPlanBadge } from '../../shared/utils/membership-plan.utils';
import { NgxMaskDirective } from 'ngx-mask';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { toNationalDigits } from 'src/app/shared/utils/format.utils';
import { UsStateSelect } from '../../shared/components/app-us-state-select/app-us-state-select';
import { GoogleMapsModule } from '@angular/google-maps';

type ConfirmAction =
  | 'verify-email'
  | 'unverify-email'
  | 'reset-password'
  | 'lock-account'
  | 'unlock-account'
  | 'password-reset-flag';

@Component({
  selector: 'app-user-detail',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, ConfirmationModalComponent, CertificationsCardComponent, NgxMaskDirective, PhoneFormatPipe, UsStateSelect, GoogleMapsModule, FormsModule],
  templateUrl: './user-detail.component.html',
  styleUrl: './user-detail.component.scss'
})
export class UserDetailComponent implements OnInit {
  userId!: string;
  user: User | null = null;
  editForm: FormGroup;
  loading = true;
  saving = false;
  errorMessage = '';
  successMessage = '';
  editMode = false;
  isLocked = false;
  loadingLockStatus = false;

  // Claims-related properties
  userClaims: UserClaims | null = null;
  loadingClaims = false;
  togglingAdmin = false;
  currentUserIsSuperAdmin = false;

  // Shared confirmation modal state (email verification, password reset, account lock)
  showConfirmModal = false;
  confirmModalAction: ConfirmAction | null = null;
  confirmModalProcessing = false;

  // Invitation-related properties
  invitations: UserInvitation[] = [];
  loadingInvitations = false;
  resendingInvitationId: string | null = null;
  sendingInvitationId: string | null = null;
  cancellingInvitationId: string | null = null;
  deletingInvitationId: string | null = null;

  pendingCoordinates: Coordinates | null = null;
  pendingManualCoordinates: Coordinates | null = null;

  lookupError: string | null = null;
  saveError: string | null = null;

  lookingUpCoordinates = false;
  savingCoordinates = false;

  mapOptions: google.maps.MapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeId: 'hybrid',
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private customClaimService: CustomClaimService,
    private toastService: ToastService,
    private userService: UserService,
    private invitationService: AdminInvitationService,
  ) {
    this.editForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      phone: [''],
      address: [''],
      city: [''],
      state: [''],
      zipCode: ['', Validators.pattern(/^\d{5}(-\d{4})?$/)],
      latitude: [null, [Validators.min(-90), Validators.max(90)]],
      longitude: [null, [Validators.min(-180), Validators.max(180)]]
    });

    // Check if current user is super admin
    this.currentUserIsSuperAdmin = this.customClaimService.isSuperAdmin();
  }

  async ngOnInit(): Promise<void> {
    this.userId = this.route.snapshot.params['id'];
    await Promise.all([
      this.loadUser(),
      this.loadUserClaims(),
    ]);
  }

  loadUser(): void {
    this.loading = true;
    this.errorMessage = '';

    this.userService.getUser(this.userId)
      .pipe(finalize(() => this.loading = false))
      .subscribe({
        next: (user) => {
          if (user === null) {
            this.errorMessage = 'Failed to load user details';
            return;
          }

          this.user = user;
          // Seed lock state from the user payload so the Lock/Unlock button
          // has a sane value before the dedicated lock-status check resolves.
          this.isLocked = !!user.isLocked;
          this.populateForm();
          this.checkLockStatus();
          // Load all invitations for this user (both profile and org invitations)
          this.loadInvitations();
        },
        error: (err) => {
          console.error('Load user error:', err);
          this.errorMessage = 'Failed to load user details';
        }
      });
  }

  updateManualCoordinate(field: 'lat' | 'lng', value: number | null): void {
    if (!this.pendingManualCoordinates || value == null) return;
    this.pendingManualCoordinates = { ...this.pendingManualCoordinates, [field]: value };
  }

  async checkLockStatus(): Promise<void> {
    this.loadingLockStatus = true;

    try {
      // The hub sends the lock status fields directly as `data` (not wrapped in `lockedStatus`).
      const status = await firstValueFrom(this.userService.getLockStatus(this.userId));

      this.isLocked = status.isLocked;

      // Keep the displayed counters in sync with the live truth.
      if (this.user) {
        this.user.failedLoginAttempts = status.failedLoginAttempts;
        this.user.lockedUntil = status.accountLockedUntil;
        this.user.isLocked = status.isLocked;
      }
    } catch (error: any) {
      console.error('Lock status error:', error);
      // Non-blocking — leave the page-level error/loading alone so the
      // rest of the user detail view still renders cleanly.
    } finally {
      this.loadingLockStatus = false;
    }
  }

  openPasswordResetModal(): void {
    if(!this.user) return;
    this.confirmModalAction = 'password-reset-flag'
    this.showConfirmModal = true
  }

  populateForm(): void {
    if (this.user) {
      this.editForm.patchValue({
        email: this.user.email,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        phone: toNationalDigits(this.user.phone),
        address: this.user.address ?? '',
        city: this.user.city ?? '',
        state: this.user.state ?? '',
        zipCode: this.user.zipCode ?? '',
        latitude: this.user.latitude ?? null,
        longitude: this.user.longitude ?? null
      });
    }
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
    if (!this.editMode) {
      this.populateForm(); // Reset form on cancel
    }
  }

  async onSubmit(): Promise<void> {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const form = this.editForm.value;
      const updated = await firstValueFrom(this.userService.updateUser(this.userId, {
        email: form.email,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: optionalText(form.phone),
        address: optionalText(form.address),
        city: optionalText(form.city),
        state: optionalText(form.state),
        zipCode: optionalText(form.zipCode),
        latitude: form.latitude ?? null,
        longitude: form.longitude ?? null,
      }));

      if (this.user && updated) {
        this.user.firstName = updated.firstName ?? this.user.firstName;
        this.user.lastName = updated.lastName ?? this.user.lastName;
        this.user.email = updated.email ?? this.user.email;
        this.user.phone = updated.phone !== undefined ? updated.phone : this.user.phone;
        this.user.address = updated.address;
        this.user.city = updated.city;
        this.user.state = updated.state;
        this.user.zipCode = updated.zipCode;
        this.user.latitude = updated.latitude;
        this.user.longitude = updated.longitude;
        this.user.updatedAt = updated.updatedAt ?? new Date();
      }

      this.toastService.success('User updated successfully');
      this.editMode = false;
    } catch (error: unknown) {
      console.error('Update user error:', error);
      this.toastService.error(errorText(error, 'Failed to update user'));
    } finally {
      this.saving = false;
    }
  }

  openLockConfirm(): void {
    if (!this.user) return;
    this.confirmModalAction = this.isLocked ? 'unlock-account' : 'lock-account';
    this.showConfirmModal = true;
  }

  private async toggleAccountLock(): Promise<void> {
    if (!this.user) return;

    const action = this.isLocked ? 'unlock' : 'lock';

    try {
      await firstValueFrom(this.userService.setLocked(this.userId, !this.isLocked));
      this.isLocked = !this.isLocked;
      this.toastService.success(`Account ${this.isLocked ? 'locked' : 'unlocked'} successfully`);
      // Refresh lock-status so failedLoginAttempts / lockedUntil reflect
      // the post-action state instead of stale pre-action values.
      await this.checkLockStatus();
    } catch (error: unknown) {
      console.error('Toggle lock error:', error);
      this.toastService.error(errorText(error, `Failed to ${action} account`));
    }
  }

  openEmailVerificationConfirm(): void {
    if (!this.user) return;
    this.confirmModalAction = this.user.emailVerified ? 'unverify-email' : 'verify-email';
    this.showConfirmModal = true;
  }

  private async toggleEmailVerification(): Promise<void> {
    if (!this.user) return;

    const newStatus = !this.user.emailVerified;

    try {
      await firstValueFrom(this.userService.setEmailVerified(this.userId, newStatus));
      this.user.emailVerified = newStatus;
      this.toastService.success(`Email ${newStatus ? 'verified' : 'unverified'} successfully`);
    } catch (error: unknown) {
      console.error('Toggle email verification error:', error);
      this.toastService.error(errorText(error, 'Failed to update email verification'));
    }
  }

  async resendVerificationEmail(): Promise<void> {
    if (!this.user) return;

    try {
      await firstValueFrom(this.userService.resendVerification(this.userId));
      this.toastService.success('Verification email sent successfully');
    } catch (error: unknown) {
      console.error('Resend verification error:', error);
      this.toastService.error(errorText(error, 'Failed to send verification email'));
    }
  }

  openPasswordResetConfirm(): void {
    if (!this.user) return;
    this.confirmModalAction = 'reset-password';
    this.showConfirmModal = true;
  }

  private async resetPassword(): Promise<void> {
    if (!this.user) return;

    try {
      await firstValueFrom(this.userService.sendResetPassword(this.userId));
      this.toastService.success('Password reset email sent successfully');
    } catch (error: unknown) {
      console.error('Reset password error:', error);
      this.toastService.error(errorText(error, 'Failed to send password reset email'));
    }
  }

  async onConfirmAction(): Promise<void> {
    if (!this.confirmModalAction) return;

    this.confirmModalProcessing = true;
    try {
      switch (this.confirmModalAction) {
        case 'verify-email':
        case 'unverify-email':
          await this.toggleEmailVerification();
          break;
        case 'reset-password':
          await this.resetPassword();
          break;
        case 'lock-account':
        case 'unlock-account':
          await this.toggleAccountLock();
          break;
        case 'password-reset-flag':
          await this.setPasswordResetFlag(!this.user?.requiresPasswordReset);
          break;
      }
    } finally {
      this.confirmModalProcessing = false;
      this.showConfirmModal = false;
      this.confirmModalAction = null;
    }
  }

  onCancelConfirm(): void {
    this.showConfirmModal = false;
    this.confirmModalAction = null;
  }

  async setPasswordResetFlag(value: boolean = true): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';
  
    try {
      const updated = await firstValueFrom(
        this.userService.requirePasswordReset(this.userId, value)
      );
      // The PUT answers with only the writable fields, so merge the flag rather than replace the user.
      if (this.user && updated) {
        this.user.requiresPasswordReset = updated.requiresPasswordReset;
      }
      this.toastService.success('User must reset their password before signing in.');
    } catch (error) {
      this.errorMessage = 'Failed to require password reset';
      console.error('Require password reset error:', error);
    }
  }
  

  async disable2FA(): Promise<void> {
    if (!this.user || !this.user.is2faEnabled) return;

    if (!confirm('Are you sure you want to disable 2FA for this user? This will require admin approval.')) {
      return;
    }

    try {
      // Mock success (there is no admin disable-2FA route yet)
      this.user.is2faEnabled = false;
      this.user.twoFactorMethod = null;
      this.toastService.success('2FA disabled successfully');
    } catch (error) {
      console.error('Disable 2FA error:', error);
      this.toastService.error('Failed to disable 2FA');
    }
  }

  // =========================================
  // Invitation Management
  // =========================================

  /**
   * Load all invitations for this user from both profile and org invitation tables.
   * Uses the unified GET /admin/users/:userId/invitations endpoint.
   */
  async loadInvitations(): Promise<void> {
    this.loadingInvitations = true;

    try {
      this.invitations = await firstValueFrom(this.userService.getInvitations(this.userId));
    } catch (error: unknown) {
      console.error('Load invitations error:', error);
      // Non-critical -- do not show error to user
    } finally {
      this.loadingInvitations = false;
    }
  }

  /**
   * Get actionable invitations (pending or expired, excludes accepted/cancelled)
   */
  get pendingInvitations(): UserInvitation[] {
    return this.invitations.filter(
      inv => inv.status === 'pending' || inv.status === 'expired'
    );
  }

  /**
   * Whether user has any organization memberships
   * When true, invitation list is read-only
   */
  get hasOrganizations(): boolean {
    return (this.userClaims?.memberships?.length ?? 0) > 0;
  }

  /**
   * Resend an invitation
   */
  async resendInvitation(invitation: UserInvitation): Promise<void> {
    this.resendingInvitationId = invitation.id;

    try {
      await firstValueFrom(this.invitationService.resendInvitation(invitation.id));
      this.toastService.success('Invitation resent successfully');
      // Reload invitations to get updated data
      await this.loadInvitations();
    } catch (error: unknown) {
      console.error('Resend invitation error:', error);
      this.toastService.error(errorText(error, 'Failed to resend invitation'));
    } finally {
      this.resendingInvitationId = null;
    }
  }

  async sendInvitation(invitation: UserInvitation): Promise<void> {
    this.sendingInvitationId = invitation.id;

    try {
      await firstValueFrom(this.invitationService.sendInvitationEmail(invitation.id));
      this.toastService.success('Invitation sent successfully');
      await this.loadInvitations();
    } catch (error: unknown) {
      console.error('Send Invitation error:', error);
      this.toastService.error(errorText(error, 'Failed to send invitation'));
    } finally {
      this.sendingInvitationId = null;
    }
  }

  /**
   * Cancel an invitation
   */
  async cancelInvitation(invitation: UserInvitation): Promise<void> {
    if (!confirm(`Cancel this invitation for ${invitation.email}?`)) {
      return;
    }

    this.cancellingInvitationId = invitation.id;

    try {
      await firstValueFrom(this.invitationService.cancelInvitation(invitation.id));
      this.toastService.success('Invitation cancelled successfully');
      await this.loadInvitations();
    } catch (error: unknown) {
      console.error('Cancel invitation error:', error);
      this.toastService.error(errorText(error, 'Failed to cancel invitation'));
    } finally {
      this.cancellingInvitationId = null;
    }
  }

  async deleteInvitation(invitation: UserInvitation): Promise<void> {
    if (!confirm(`Delete this invitation for ${invitation.email}?`)) {
      return;
    }

    this.deletingInvitationId = invitation.id;

    try {
      await firstValueFrom(this.invitationService.deleteInvitation(invitation.id));
      this.toastService.success('Invitation deleted successfully');
      await this.loadInvitations();
    } catch (error: unknown) {
      console.error('Delete invitation error:', error);
      this.toastService.error(errorText(error, 'Failed to delete invitation'));
    } finally {
      this.deletingInvitationId = null;
    }
  }

  async lookupCoordinates(): Promise<void> {
    if (!this.user) return;

    this.lookupError = null;
    this.lookingUpCoordinates = true;

    try {
      const coords = await firstValueFrom(this.userService.geocode(this.user.id));

      if (coords) {
        this.pendingCoordinates = coords;
      } else {
        this.lookupError = 'Failed to lookup coordinates';
        this.pendingManualCoordinates = {
          lat: this.user.latitude ?? 0,
          lng: this.user.longitude ?? 0
        };
      }
    } catch (error: unknown) {
      console.error('Lookup coordinates error:', error);
      this.lookupError = errorText(error, 'Failed to lookup coordinates');
      this.pendingManualCoordinates = {
        lat: this.user.latitude ?? 0,
        lng: this.user.longitude ?? 0
      };
    } finally {
      this.lookingUpCoordinates = false;
    }
  }

  async confirmedCoordinates(): Promise<void> {
    const coords = this.pendingCoordinates ?? this.pendingManualCoordinates;
    if (!this.user || !coords) return;

    this.savingCoordinates = true;
    this.saveError = null;

    try {
      await firstValueFrom(this.userService.updateCoordinates(this.user.id, coords));

      this.user.latitude = coords.lat;
      this.user.longitude = coords.lng;
      this.user.updatedAt = new Date();
      this.editForm.patchValue({
        latitude: coords.lat,
        longitude: coords.lng
      });
      this.pendingCoordinates = null;
      this.pendingManualCoordinates = null;
    } catch (error: unknown) {
      console.error('Confirm coordinates error:', error);
      this.saveError = errorText(error, 'Failed to update coordinates');
    } finally {
      this.savingCoordinates = false;
    }
  }

  declinedCoordinates(): void {
    if (this.savingCoordinates) return;

    this.lookupError = null;
    this.saveError = null;
    this.pendingCoordinates = null;
    this.pendingManualCoordinates = null;
  }

  /**
   * Get source badge class
   */
  getSourceBadgeClass(source: string): string {
    return source === 'organization' ? 'bg-primary' : 'bg-info';
  }

  /**
   * Get source display text
   */
  getSourceText(source: string): string {
    return source === 'organization' ? 'Organization' : 'Profile';
  }

  /**
   * Get status badge class
   */
  getStatusBadgeClass(status: string | null | undefined): string {
    switch (status) {
      case 'pending': return 'bg-warning text-dark';
      case 'accepted': return 'bg-success';
      case 'cancelled': return 'bg-secondary';
      case 'expired': return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  /**
   * Get email status badge class
   */
  getEmailStatusBadgeClass(status: string | null | undefined): string {
    switch (status) {
      case 'sent': return 'bg-success';
      case 'failed': return 'bg-danger';
      case 'never_sent':
      default: return 'bg-secondary';
    }
  }

  /**
   * Get email status display text
   */
  getEmailStatusText(status: string | null | undefined): string {
    switch (status) {
      case 'sent': return 'Email Sent';
      case 'failed': return 'Email Failed';
      case 'never_sent':
      default: return 'Not Sent';
    }
  }

  /**
   * Check if invitation is expired
   */
  isInvitationExpired(invitation: UserInvitation): boolean {
    if (!invitation.expiresAt) return false;
    return new Date() > new Date(invitation.expiresAt);
  }

  formatInvitationType(type: string | null | undefined, invitation?: UserInvitation): string {
    if (!type) {
      // For organization invitations, show role instead
      if (invitation?.source === 'organization' && invitation.role != null) {
        if ((invitation.role & MembershipRoles.PilotInCharge) === MembershipRoles.PilotInCharge) return 'Pilot in Command';
        if ((invitation.role & MembershipRoles.Pilot) === MembershipRoles.Pilot) return 'Pilot';
        return 'Member';
      }
      return 'Organization Invite';
    }
    return type.split('_').join(' ');
  }

  goBack(): void {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl');
    if (returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//')) {
      this.router.navigateByUrl(returnUrl);
      return;
    }
    this.router.navigate(['/users']);
  }

  goBackText(): string {
    return this.route.snapshot.queryParamMap.get('returnLabel') ?? 'Users';
  }

  readonly getLoginPlatformBadge = getLoginPlatformBadge;
  readonly getMembershipPlanBadge = getMembershipPlanBadge;

  formatDate(dateString: Date | string | null | undefined): string {
    if (!dateString) return 'Never';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  // =========================================
  // Roles & Permissions
  // =========================================

  async loadUserClaims(): Promise<void> {
    this.loadingClaims = true;

    try {
      this.userClaims = await firstValueFrom(this.userService.getClaims(this.userId));
    } catch (error: unknown) {
      console.error('Load user claims error:', error);
      // Don't show error for claims - user profile still loaded
    } finally {
      this.loadingClaims = false;
    }
  }

  async confirmToggleAdminRole(): Promise<void> {
    if (!this.userClaims || !this.currentUserIsSuperAdmin) return;

    const newAdminStatus = !this.userClaims.isAdmin;

    this.togglingAdmin = true;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(this.userService.setAdminRole(this.userId, newAdminStatus));

      this.userClaims.isAdmin = response.isAdmin;
      this.userClaims.isSuperAdmin = response.isSuperAdmin;

      // Update global roles display
      this.userClaims.globalRoles = [];
      if (response.isAdmin) this.userClaims.globalRoles.push('Admin');
      if (response.isSuperAdmin) this.userClaims.globalRoles.push('Super Admin');

      this.toastService.success(
        newAdminStatus ? 'Admin access granted successfully' : 'Admin access revoked successfully'
      );

      // Close the modal
      this.closeModal('adminRoleModal');
    } catch (error: unknown) {
      console.error('Toggle admin role error:', error);
      this.toastService.error(errorText(error, 'Failed to update admin role'));
    } finally {
      this.togglingAdmin = false;
    }
  }

  private closeModal(modalId: string): void {
    const modalElement = document.getElementById(modalId);
    if (modalElement) {
      // Use Bootstrap's modal API to close it
      const modalInstance = (window as any).bootstrap?.Modal?.getInstance(modalElement);
      if (modalInstance) {
        modalInstance.hide();
      } else {
        // Fallback: manually trigger close button
        const closeBtn = modalElement.querySelector('[data-bs-dismiss="modal"]') as HTMLElement;
        closeBtn?.click();
      }
    }
  }
}

/** The hub's error message for a failed request, or `fallback`. */
function errorText(error: unknown, fallback: string): string {
  return ApiError.fromHttpError(error).errors[0] ?? fallback;
}
